import { AiOutlineLoading } from "react-icons/ai";
import { Alert, Badge, Button, Checkbox, TextInput } from "flowbite-react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { ADMIN } from "../../config/apiConfig";
import axios from "../../config/axiosInstance";
import { useEffect, useState, useMemo } from "react";
import { HiChevronDown, HiInformationCircle } from "react-icons/hi";
import { useQuery, useQueryClient } from "react-query";
import Input from "../inputs/Input";
import DrawerLoading from "../DrawerLoading";
import CurrencyInput from "../inputs/CurrencyInput";
import CustomSwitch from "../inputs/CustomSwitch";
import { formatToReal } from "../../utils/utils";

const StatChip = ({ label, value }) => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-50 border border-gray-200">
    <span className="text-xs text-gray-500">{label}</span>
    <span className="text-sm font-semibold text-gray-800">{value}</span>
  </div>
);

const TabButton = ({
  active,
  onClick,
  title,
  subtitle,
  count,
  tone = "emerald",
  disabled = false,
}) => {
  const toneMap = {
    emerald: {
      active: "from-emerald-500 to-teal-500",
      ring: "ring-emerald-200",
      text: "text-emerald-700",
      badge: "bg-emerald-50 text-emerald-700",
    },
    amber: {
      active: "from-amber-500 to-orange-500",
      ring: "ring-amber-200",
      text: "text-amber-700",
      badge: "bg-amber-50 text-amber-700",
    },
  };
  const palette = toneMap[tone] || toneMap.emerald;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={[
        "w-full text-left rounded-xl border px-4 py-3 transition-all",
        "flex items-center justify-between gap-4",
        disabled ? "opacity-50 cursor-not-allowed bg-gray-50" : "hover:shadow-sm",
        active
          ? `bg-gradient-to-r ${palette.active} text-white border-transparent shadow-md ring-2 ${palette.ring}`
          : "bg-white/80 border-gray-200",
      ].join(" ")}
    >
      <div className="min-w-0">
        <div
          className={[
            "text-sm md:text-base font-semibold truncate",
            active ? "text-white" : palette.text,
          ].join(" ")}
        >
          {title}
        </div>
        <div
          className={[
            "text-xs truncate",
            active ? "text-white/85" : "text-gray-500",
          ].join(" ")}
        >
          {subtitle}
        </div>
      </div>

      <div
        className={[
          "shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold",
          active ? "bg-white/20 text-white" : palette.badge,
        ].join(" ")}
      >
        {count} ativas
      </div>
    </button>
  );
};

const CustomAccordionItem = ({ title, badge, open, onToggle, children }) => (
  <div className="rounded-xl border border-gray-200 bg-white/80 overflow-hidden">
    <button
      type="button"
      onClick={onToggle}
      className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left hover:bg-gray-50/70 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm md:text-base font-semibold text-gray-800 truncate">
          {title}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {badge}
        <HiChevronDown
          className={[
            "h-4 w-4 text-gray-500 transition-transform",
            open ? "rotate-180" : "",
          ].join(" ")}
        />
      </div>
    </button>
    {open && <div className="px-4 pb-4 pt-1">{children}</div>}
  </div>
);

const QueryRow = ({
  name,
  basePrice,
  fieldPathActive,
  fieldPathPrice,
  control,
  setValue,
}) => {
  const isActive = !!useWatch({ control, name: fieldPathActive });
  const typedPrice = Number(useWatch({ control, name: fieldPathPrice }) ?? 0);
  const base = Number(basePrice || 0);
  const marginPct = base > 0 ? ((typedPrice - base) / base) * 100 : null;
  const formatPct = (n) => `${n.toFixed(2)}%`;

  const marginTone =
    !isActive || marginPct === null
      ? "text-gray-400 border-gray-200 bg-gray-50"
      : marginPct < 10
      ? "text-orange-600 border-orange-200 bg-orange-50/60"
      : "text-emerald-600 border-emerald-200 bg-emerald-50/60";

  return (
    <div className="border-b border-gray-200 py-3 md:py-3">
      <div className="flex items-center justify-between gap-4">
        <Controller
          name={fieldPathActive}
          control={control}
          render={({ field }) => (
            <CustomSwitch
              id={`switch-${fieldPathActive}`}
              checked={!!field.value}
              onChange={(value) => {
                field.onChange(value);
                if (value && (typedPrice === 0 || Number.isNaN(typedPrice))) {
                  setValue(fieldPathPrice, base || 0, { shouldDirty: true });
                }
              }}
              label={name}
              subtitle={`Custo base: ${formatToReal(base)}`}
              size="md"
              color="emerald"
            />
          )}
        />

        <div className="flex items-center gap-2">
          <div className="min-w-[170px]">
            <CurrencyInput
              name={fieldPathPrice}
              className="w-full"
              label="Preço"
              control={control}
              disabled={!isActive}
            />
          </div>

          <div
            className={[
              "shrink-0 px-2.5 py-1.5 rounded-full text-2xs font-semibold border flex items-center justify-center min-w-[52px] h-8",
              marginTone,
            ].join(" ")}
            title={
              marginPct === null
                ? "Sem custo base para calcular"
                : `Margem sobre o custo (${formatToReal(base)})`
            }
          >
            {marginPct === null ? "—%" : <>Margem: {formatPct(marginPct)}</>}
          </div>
        </div>
      </div>

      <div className="mt-1">
        {!isActive ? (
          <div className="text-[11px] text-gray-500 -mt-2">
            Ative para editar o preço
          </div>
        ) : null}
      </div>
    </div>
  );
};

const TableQueriesForm = ({
  categories,
  fetchTables,
  onClose,
  selectedTable,
  type,
}) => {
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("main");
  const [openMainPanels, setOpenMainPanels] = useState({});
  const [openAddPanels, setOpenAddPanels] = useState({});
  const [searchTerms, setSearchTerms] = useState({});
  const [showOnlyActiveMap, setShowOnlyActiveMap] = useState({});

  const { isLoading: isLoadingCompanyQueries, data: tableQueries } = useQuery(
    ["tableQueries", selectedTable?.id],
    async () => {
      if (!selectedTable) return null;
      const { data: items } = await axios.get(
        `${ADMIN.GET_TABLE_QUERIES}/${selectedTable?.id}`
      );
      return items.data;
    },
    { enabled: !!selectedTable }
  );

  const { register, handleSubmit, reset, setValue, watch, control } = useForm();
  const watchedCategories = watch("categories", []);

  const sortByName = (arr) =>
    [...arr].sort((a, b) =>
      String(a.name || "").localeCompare(String(b.name || ""), "pt", {
        sensitivity: "base",
      })
    );

  const activeCountsByCategory = useMemo(() => {
    return (watchedCategories || []).map((cat) => {
      const qs = cat?.queries || [];
      return qs.reduce((acc, q) => acc + (q?.isActive ? 1 : 0), 0);
    });
  }, [watchedCategories]);

  const totalActive = useMemo(
    () => activeCountsByCategory.reduce((sum, n) => sum + n, 0),
    [activeCountsByCategory]
  );

  const tabActiveCounts = useMemo(() => {
    let main = 0;
    let add = 0;
    categories?.forEach((category, idxCat) => {
      category?.Queries?.forEach((query, idxQuery) => {
        const isActive =
          watchedCategories?.[idxCat]?.queries?.[idxQuery]?.isActive ?? false;
        if (!isActive) return;
        if (query?.isAdditional) add += 1;
        else main += 1;
      });
    });
    return { main, add };
  }, [categories, watchedCategories]);

  const hasMainQueries = useMemo(
    () =>
      categories?.some((category) =>
        category?.Queries?.some((query) => !query.isAdditional)
      ),
    [categories]
  );

  const hasAdditionalQueries = useMemo(
    () =>
      categories?.some((category) =>
        category?.Queries?.some((query) => !!query.isAdditional)
      ),
    [categories]
  );

  useEffect(() => {
    if (activeTab === "main" && !hasMainQueries && hasAdditionalQueries) {
      setActiveTab("add");
    }
    if (activeTab === "add" && !hasAdditionalQueries && hasMainQueries) {
      setActiveTab("main");
    }
  }, [activeTab, hasMainQueries, hasAdditionalQueries]);

  const toggleOnlyActive = (key) =>
    setShowOnlyActiveMap((prev) => ({ ...prev, [key]: !prev[key] }));

  const toggleAllInCategory = (idxCat, queryIndices, value) => {
    queryIndices.forEach((idxQuery) => {
      setValue(`categories[${idxCat}].queries[${idxQuery}].isActive`, value, {
        shouldDirty: true,
      });
    });
  };

  // ===== Prefill =====
  useEffect(() => {
    reset();
    setError(null);

    if (selectedTable) {
      setValue("name", selectedTable?.name);
      setValue("vcm", selectedTable?.vcm);
      setValue("hasSurvey", selectedTable?.hasSurvey);
      setValue("hasCV", selectedTable?.hasCV);
      setValue("priceSurvey", selectedTable?.priceSurvey);
      setValue("priceCV", selectedTable?.priceCV);
    }

    categories?.forEach((category, catIdx) => {
      category?.Queries?.forEach((query, index) => {
        setValue(`categories[${catIdx}].queries[${index}].QueryId`, query.id);

        const tableQuery =
          tableQueries?.find((cq) => cq.QueryId === query.id) || null;

        if (tableQuery) {
          setValue(`categories[${catIdx}].queries[${index}].isActive`, true);
          setValue(
            `categories[${catIdx}].queries[${index}].price`,
            tableQuery.price
          );
        } else {
          setValue(
            `categories[${catIdx}].queries[${index}].price`,
            query.price
          );
        }
      });
    });
  }, [tableQueries, selectedTable, type, categories, reset, setValue]);

  // ===== Submit =====
  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);

      let flat = [];
      (data.categories || []).forEach((cat) => {
        (cat.queries || []).forEach((q) => flat.push(q));
      });
      data.queries = flat.filter((q) => q.isActive);

      if (data.vcm === "") delete data.vcm;

      if (selectedTable) {
        await axios.put(`${ADMIN.GET_TABLE_QUERIES}/${selectedTable.id}`, data);
        queryClient.invalidateQueries(["tableQueries", selectedTable.id], {
          exact: true,
        });
        queryClient.refetchQueries(["tableQueries", selectedTable.id], {
          active: true,
          inactive: true,
        });
      } else {
        await axios.post(`${ADMIN.GET_TABLE}`, data);
      }

      onClose(false);
      fetchTables?.();
    } catch (err) {
      setError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== Render =====
  return (
    <>
      <form className="mb-6 space-y-4" onSubmit={handleSubmit(onSubmit)}>
        {/* Header card */}
        <div className="rounded-2xl border shadow-sm p-4">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div className="flex-1">
              <Input
                className="mb-4"
                label="Nome da tabela"
                register={register}
                name="name"
                required={true}
              />
              <div className="grid grid-cols-1 gap-3">
                <div>
                  <CurrencyInput
                    className="mb-1"
                    label="VCM"
                    register={register}
                    name="vcm"
                    control={control}
                  />
                  <div className="mt-1 text-[11px] text-gray-500">
                    VCM: Valor de custo mínimo mensal
                  </div>
                </div>
              </div>
            </div>

            {type !== "CREATE" && (
              <div className="rounded-xl border bg-white p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox id="accept" {...register("updateAll")} />
                  <span className="text-sm">
                    Atualizar preços para todos os clientes desta tabela
                  </span>
                </label>
                <div className="mt-1 text-[11px] text-gray-500">
                  Esta ação aplica imediatamente as alterações.
                </div>
              </div>
            )}
          </div>
        </div>

        {categories?.length > 0 ? (
          <>
            <div className="mb-4">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <h2 className="text-lg md:text-xl font-bold text-gray-900">
                    Permissões da tabela
                  </h2>
                  <p className="text-sm text-gray-500">
                    {type === "CREATE"
                      ? "Defina as consultas e preços da nova tabela."
                      : "Edite as permissões e preços desta tabela."}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <StatChip label="Ativas" value={totalActive} />
                  <StatChip
                    label="Categorias"
                    value={(categories || []).length}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="rounded-2xl border border-gray-200 bg-gradient-to-br from-white via-white to-emerald-50/40 p-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <TabButton
                    active={activeTab === "main"}
                    onClick={() => setActiveTab("main")}
                    title="Consultas principais"
                    subtitle="Essenciais do contrato"
                    count={tabActiveCounts.main}
                    tone="emerald"
                    disabled={!hasMainQueries}
                  />
                  <TabButton
                    active={activeTab === "add"}
                    onClick={() => setActiveTab("add")}
                    title="Consultas adicionais"
                    subtitle="Extras sob demanda"
                    count={tabActiveCounts.add}
                    tone="amber"
                    disabled={!hasAdditionalQueries}
                  />
                </div>
              </div>

              {activeTab === "main" ? (
                <div>
                  <div className="w-full space-y-2">
                    {categories.map((category, idxCat) => {
                      const keyMain = `main-${idxCat}`;
                      const onlyActiveMain = !!showOnlyActiveMap[keyMain];
                      const term = (searchTerms[idxCat] ?? "").toLowerCase();
                      const isOpen = !!openMainPanels[keyMain];

                      let filtered = (category.Queries || []).filter(
                        (q) =>
                          !q.isAdditional &&
                          q.name.toLowerCase().includes(term)
                      );

                      if (onlyActiveMain) {
                        const watchedCat =
                          watchedCategories?.[idxCat]?.queries || [];
                        filtered = filtered.filter((q) => {
                          const realIndex = category.Queries.findIndex(
                            (qq) => qq.id === q.id
                          );
                          return !!watchedCat?.[realIndex]?.isActive;
                        });
                      }

                      filtered = sortByName(filtered);

                      const watchedCat =
                        watchedCategories?.[idxCat]?.queries || [];
                      const activeCountMain = filtered.reduce((acc, q) => {
                        const idxReal = category.Queries.findIndex(
                          (qq) => qq.id === q.id
                        );
                        const isActive =
                          watchedCat?.[idxReal]?.isActive ?? false;
                        return acc + (isActive ? 1 : 0);
                      }, 0);

                      if (filtered.length === 0) return null;

                      return (
                        <CustomAccordionItem
                          key={`main-${idxCat}`}
                          title={category.name}
                          open={isOpen}
                          onToggle={() =>
                            setOpenMainPanels((prev) => ({
                              ...prev,
                              [keyMain]: !prev[keyMain],
                            }))
                          }
                          badge={
                            <Badge color="info">{activeCountMain} ativas</Badge>
                          }
                        >
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                              <div className="w-full md:w-80">
                                <TextInput
                                  sizing="sm"
                                  placeholder="Filtrar consultas principais desta categoria..."
                                  value={searchTerms[idxCat] ?? ""}
                                  onChange={(e) =>
                                    setSearchTerms((prev) => ({
                                      ...prev,
                                      [idxCat]: e.target.value,
                                    }))
                                  }
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                  Digite parte do nome para filtrar.
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {(searchTerms[idxCat] ?? "").length > 0 && (
                                  <Button
                                    color="light"
                                    size="xs"
                                    onClick={() =>
                                      setSearchTerms((prev) => ({
                                        ...prev,
                                        [idxCat]: "",
                                      }))
                                    }
                                  >
                                    Limpar filtro
                                  </Button>
                                )}

                                <Button
                                  color={onlyActiveMain ? "success" : "light"}
                                  size="xs"
                                  onClick={() => toggleOnlyActive(keyMain)}
                                  title="Exibir somente consultas ativas desta categoria"
                                >
                                  {onlyActiveMain
                                    ? "Mostrando só ativas"
                                    : "Mostrar só ativas"}
                                </Button>

                                <Button
                                  color="success"
                                  size="xs"
                                  onClick={() => {
                                    const indices = category.Queries.map(
                                      (q, i) => ({
                                        i,
                                        name: q.name.toLowerCase(),
                                        isAdditional: !!q.isAdditional,
                                      })
                                    )
                                      .filter(
                                        ({ name, isAdditional }) =>
                                          !isAdditional &&
                                          name.includes(term)
                                      )
                                      .map(({ i }) => i);
                                    toggleAllInCategory(idxCat, indices, true);
                                  }}
                                >
                                  Selecionar Todas
                                </Button>

                                <Button
                                  color="failure"
                                  size="xs"
                                  onClick={() => {
                                    const indices = category.Queries.map(
                                      (q, i) => ({
                                        i,
                                        name: q.name.toLowerCase(),
                                        isAdditional: !!q.isAdditional,
                                      })
                                    )
                                      .filter(
                                        ({ name, isAdditional }) =>
                                          !isAdditional &&
                                          name.includes(term)
                                      )
                                      .map(({ i }) => i);
                                    toggleAllInCategory(idxCat, indices, false);
                                  }}
                                >
                                  Limpar todas
                                </Button>
                              </div>
                            </div>

                            <div className="flex flex-col">
                              {filtered.length > 0 ? (
                                filtered.map((query) => {
                                  const realIndex =
                                    category.Queries.findIndex(
                                      (q) => q.id === query.id
                                    );
                                  return (
                                    <QueryRow
                                      key={`main-${query.id}-${realIndex}`}
                                      name={query.name}
                                      basePrice={query.price}
                                      fieldPathActive={`categories[${idxCat}].queries[${realIndex}].isActive`}
                                      fieldPathPrice={`categories[${idxCat}].queries[${realIndex}].price`}
                                      control={control}
                                      setValue={setValue}
                                    />
                                  );
                                })
                              ) : (
                                <div className="text-sm md:text-[15px] text-center py-2 text-gray-500">
                                  Nenhuma consulta principal corresponde ao
                                  filtro.
                                </div>
                              )}
                            </div>
                          </div>
                        </CustomAccordionItem>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div>
                  <div className="w-full space-y-2">
                    {categories.map((category, idxCat) => {
                      const keyAdd = `add-${idxCat}`;
                      const onlyActiveAdd = !!showOnlyActiveMap[keyAdd];
                      const term = (
                        searchTerms[`add-${idxCat}`] ?? ""
                      ).toLowerCase();
                      const isOpen = !!openAddPanels[keyAdd];

                      let filtered = (category.Queries || []).filter(
                        (q) =>
                          !!q.isAdditional &&
                          q.name.toLowerCase().includes(term)
                      );

                      if (onlyActiveAdd) {
                        const watchedCat =
                          watchedCategories?.[idxCat]?.queries || [];
                        filtered = filtered.filter((q) => {
                          const realIndex = category.Queries.findIndex(
                            (qq) => qq.id === q.id
                          );
                          return !!watchedCat?.[realIndex]?.isActive;
                        });
                      }

                      filtered = sortByName(filtered);

                      const watchedCat =
                        watchedCategories?.[idxCat]?.queries || [];
                      const activeCountAdd = filtered.reduce((acc, q) => {
                        const idxReal = category.Queries.findIndex(
                          (qq) => qq.id === q.id
                        );
                        const isActive =
                          watchedCat?.[idxReal]?.isActive ?? false;
                        return acc + (isActive ? 1 : 0);
                      }, 0);

                      if (filtered.length === 0) return null;

                      return (
                        <CustomAccordionItem
                          key={`add-${idxCat}`}
                          title={category.name}
                          open={isOpen}
                          onToggle={() =>
                            setOpenAddPanels((prev) => ({
                              ...prev,
                              [keyAdd]: !prev[keyAdd],
                            }))
                          }
                          badge={
                            <Badge color="purple">
                              {activeCountAdd} ativas
                            </Badge>
                          }
                        >
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                              <div className="w-full md:w-80">
                                <TextInput
                                  sizing="sm"
                                  placeholder="Filtrar consultas adicionais desta categoria..."
                                  value={searchTerms[`add-${idxCat}`] ?? ""}
                                  onChange={(e) =>
                                    setSearchTerms((prev) => ({
                                      ...prev,
                                      [`add-${idxCat}`]: e.target.value,
                                    }))
                                  }
                                />
                                <div className="mt-1 text-xs text-gray-500">
                                  Digite parte do nome para filtrar.
                                </div>
                              </div>

                              <div className="flex items-center gap-2">
                                {(searchTerms[`add-${idxCat}`] ?? "").length >
                                  0 && (
                                  <Button
                                    color="light"
                                    size="xs"
                                    onClick={() =>
                                      setSearchTerms((prev) => ({
                                        ...prev,
                                        [`add-${idxCat}`]: "",
                                      }))
                                    }
                                  >
                                    Limpar filtro
                                  </Button>
                                )}

                                <Button
                                  color={onlyActiveAdd ? "success" : "light"}
                                  size="xs"
                                  onClick={() => toggleOnlyActive(keyAdd)}
                                  title="Exibir somente consultas ativas desta categoria"
                                >
                                  {onlyActiveAdd
                                    ? "Mostrando só ativas"
                                    : "Mostrar só ativas"}
                                </Button>

                                <Button
                                  color="success"
                                  size="xs"
                                  onClick={() => {
                                    const indices = category.Queries.map(
                                      (q, i) => ({
                                        i,
                                        name: q.name.toLowerCase(),
                                        isAdditional: !!q.isAdditional,
                                      })
                                    )
                                      .filter(
                                        ({ name, isAdditional }) =>
                                          isAdditional &&
                                          name.includes(term)
                                      )
                                      .map(({ i }) => i);
                                    toggleAllInCategory(idxCat, indices, true);
                                  }}
                                >
                                  Selecionar Todas
                                </Button>

                                <Button
                                  color="failure"
                                  size="xs"
                                  onClick={() => {
                                    const indices = category.Queries.map(
                                      (q, i) => ({
                                        i,
                                        name: q.name.toLowerCase(),
                                        isAdditional: !!q.isAdditional,
                                      })
                                    )
                                      .filter(
                                        ({ name, isAdditional }) =>
                                          isAdditional &&
                                          name.includes(term)
                                      )
                                      .map(({ i }) => i);
                                    toggleAllInCategory(idxCat, indices, false);
                                  }}
                                >
                                  Limpar todas
                                </Button>
                              </div>
                            </div>

                            <div className="flex flex-col">
                              {filtered.length > 0 ? (
                                filtered.map((query) => {
                                  const realIndex =
                                    category.Queries.findIndex(
                                      (q) => q.id === query.id
                                    );
                                  return (
                                    <QueryRow
                                      key={`add-${query.id}-${realIndex}`}
                                      name={query.name}
                                      basePrice={query.price}
                                      fieldPathActive={`categories[${idxCat}].queries[${realIndex}].isActive`}
                                      fieldPathPrice={`categories[${idxCat}].queries[${realIndex}].price`}
                                      control={control}
                                      setValue={setValue}
                                    />
                                  );
                                })
                              ) : (
                                <div className="text-sm md:text-[15px] text-center py-2 text-gray-500">
                                  Nenhuma consulta adicional corresponde ao
                                  filtro.
                                </div>
                              )}
                            </div>
                          </div>
                        </CustomAccordionItem>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end">
              <Button
                type="submit"
                className="w-full md:w-auto md:min-w-[220px] primary mt-4"
                isProcessing={isSubmitting}
                processingSpinner={
                  <AiOutlineLoading className="h-6 w-6 animate-spin" />
                }
                disabled={isSubmitting}
              >
                {type === "CREATE" ? "Criar tabela" : "Atualizar tabela"}
              </Button>
            </div>

            {error && (
              <Alert
                color="failure"
                icon={HiInformationCircle}
                className="mt-3"
              >
                Erro ao {type === "CREATE" ? "criar" : "atualizar"} a tabela.
              </Alert>
            )}
          </>
        ) : (
          <div className="text-sm md:text-md text-center rounded-xl border bg-white p-6">
            Não há nenhuma categoria cadastrada. <br />
            Comece cadastrando as categorias primeiro.
          </div>
        )}
      </form>

      {isLoadingCompanyQueries && <DrawerLoading />}
    </>
  );
};

export default TableQueriesForm;
